import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Settings as SettingsIcon, Edit2, Check, X } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


export default function SettingsManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [readerTypes, setReaderTypes] = useState([]);
  const [billingFrequencies, setBillingFrequencies] = useState([]);
  const [newReaderType, setNewReaderType] = useState('');
  const [newReaderCost, setNewReaderCost] = useState('');
  const [newReaderHasSim, setNewReaderHasSim] = useState(false);
  const [newReaderSimCost, setNewReaderSimCost] = useState('');
  const [newBillingFreq, setNewBillingFreq] = useState('');
  const [editingReader, setEditingReader] = useState(null);
  // Inline delete confirmations (replaces window.confirm which is blocked in preview/prod)
  const [confirmDeleteReaderId, setConfirmDeleteReaderId] = useState(null);
  const [confirmDeleteFreqId, setConfirmDeleteFreqId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [readerTypesRes, billingFreqsRes] = await Promise.all([
        api.get('/reader-types-full'),
        api.get('/billing-frequencies')
      ]);
      
      setReaderTypes(readerTypesRes.data.reader_types || []);
      setBillingFrequencies(billingFreqsRes.data.billing_frequencies || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReaderType = async () => {
    if (!newReaderType.trim()) {
      toast.error('Please enter a reader type name');
      return;
    }

    const costValue = parseFloat(newReaderCost) || 0;
    if (costValue < 0) {
      toast.error('Cost per unit cannot be negative');
      return;
    }

    const simCostValue = parseFloat(newReaderSimCost) || 0;
    if (simCostValue < 0) {
      toast.error('SIM cost cannot be negative');
      return;
    }

    try {
      await api.post('/admin/reader-types', 
        { 
          name: newReaderType,
          monthly_cost: costValue,
          has_sim: newReaderHasSim,
          sim_cost: simCostValue
        }
      );
      
      toast.success('Reader type added successfully');
      setNewReaderType('');
      setNewReaderCost('');
      setNewReaderHasSim(false);
      setNewReaderSimCost('');
      loadData();
      
      // Signal Calculator to reload reference data
      localStorage.setItem('reload_reference_data', 'true');
    } catch (error) {
      console.error('Error adding reader type:', error);
      toast.error(error.response?.data?.detail || 'Failed to add reader type');
    }
  };

  const handleDeleteReaderType = async (readerTypeId) => {
    try {
      await api.delete(`/admin/reader-types/${readerTypeId}`);
      toast.success('Reader type deleted successfully');
      setConfirmDeleteReaderId(null);
      loadData();
      localStorage.setItem('reload_reference_data', 'true');
    } catch (error) {
      console.error('Error deleting reader type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete reader type');
    }
  };

  const handleUpdateReaderType = async () => {
    if (!editingReader || !editingReader.name.trim()) {
      toast.error('Please enter a reader type name');
      return;
    }

    const costValue = parseFloat(editingReader.monthly_cost ?? editingReader.cost_per_unit) || 0;
    if (costValue < 0) {
      toast.error('Cost per unit cannot be negative');
      return;
    }

    const simCostValue = parseFloat(editingReader.sim_cost) || 0;
    if (simCostValue < 0) {
      toast.error('SIM cost cannot be negative');
      return;
    }

    try {
      await api.put(`/admin/reader-types/${editingReader.id}`, 
        { 
          name: editingReader.name,
          monthly_cost: costValue,
          has_sim: editingReader.has_sim || false,
          sim_cost: simCostValue
        }
      );
      
      toast.success('Reader type updated successfully');
      setEditingReader(null);
      loadData();
      
      // Signal Calculator to reload reference data
      localStorage.setItem('reload_reference_data', 'true');
    } catch (error) {
      console.error('Error updating reader type:', error);
      toast.error(error.response?.data?.detail || 'Failed to update reader type');
    }
  };

  const handleAddBillingFreq = async () => {
    if (!newBillingFreq.trim()) {
      toast.error('Please enter a billing frequency name');
      return;
    }

    try {
      await api.post('/admin/billing-frequencies', 
        { name: newBillingFreq }
      );
      
      toast.success('Billing frequency added successfully');
      setNewBillingFreq('');
      loadData();
    } catch (error) {
      console.error('Error adding billing frequency:', error);
      toast.error(error.response?.data?.detail || 'Failed to add billing frequency');
    }
  };

  const handleDeleteBillingFreq = async (billingFreqId) => {
    try {
      await api.delete(`/admin/billing-frequencies/${billingFreqId}`);
      toast.success('Billing frequency deleted successfully');
      setConfirmDeleteFreqId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting billing frequency:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete billing frequency');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <SettingsIcon className="w-6 h-6 text-indigo-600" />
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Settings Manager</h1>
                  <p className="text-sm text-slate-600">Manage reader types and billing frequencies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reader Types */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Reader Types</h2>
            
            {/* Add New */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <label className="block text-sm font-medium text-slate-700 mb-2">Add New Reader Type</label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newReaderType}
                  onChange={(e) => setNewReaderType(e.target.value)}
                  placeholder="Reader type name..."
                  className="input-field w-full"
                />
                <input
                  type="number"
                  value={newReaderCost}
                              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                  onChange={(e) => setNewReaderCost(e.target.value)}
                  placeholder="Monthly cost ($)"
                  step="0.01"
                  min="0"
                  className="input-field w-full"
                />
                
                {/* SIM Card Section */}
                <div className="border-t border-slate-200 pt-3">
                  <label className="flex items-center gap-2 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newReaderHasSim}
                      onChange={(e) => setNewReaderHasSim(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Has SIM Card</span>
                  </label>
                  
                  {newReaderHasSim && (
                    <input
                      type="number"
                      value={newReaderSimCost}
                                  onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                      onChange={(e) => setNewReaderSimCost(e.target.value)}
                      placeholder="SIM cost per month ($)"
                      step="0.01"
                      min="0"
                      className="input-field w-full"
                    />
                  )}
                </div>
                
                <button onClick={handleAddReaderType} className="btn-primary w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Reader Type
                </button>
              </div>
            </div>

            {/* List */}
            <div className="space-y-2">
              {readerTypes.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No reader types found</p>
              ) : (
                readerTypes.map((type, index) => (
                  <div key={type.id || index} className="p-3 bg-white rounded-lg border border-slate-200">
                    {editingReader?.id === type.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editingReader.name}
                          onChange={(e) => setEditingReader({ ...editingReader, name: e.target.value })}
                          className="input-field w-full"
                          placeholder="Reader type name"
                        />
                        <input
                          type="number"
                          value={editingReader.monthly_cost ?? editingReader.cost_per_unit ?? ''}
                          onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                          onClick={(e) => e.target.select()}
                          onChange={(e) => setEditingReader({ ...editingReader, monthly_cost: e.target.value })}
                          className="input-field w-full"
                          placeholder="Monthly cost ($)"
                          step="0.01"
                          min="0"
                        />
                        
                        {/* SIM Card Section */}
                        <div className="border-t border-slate-200 pt-3">
                          <label className="flex items-center gap-2 mb-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingReader.has_sim || false}
                              onChange={(e) => setEditingReader({ ...editingReader, has_sim: e.target.checked })}
                              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm font-medium text-slate-700">Has SIM Card</span>
                          </label>
                          
                          {editingReader.has_sim && (
                            <input
                              type="number"
                              value={editingReader.sim_cost || 0}
                                          onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                              onChange={(e) => setEditingReader({ ...editingReader, sim_cost: e.target.value })}
                              className="input-field w-full"
                              placeholder="SIM cost per month ($)"
                              step="0.01"
                              min="0"
                            />
                          )}
                        </div>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={handleUpdateReaderType}
                            className="btn-primary flex-1 inline-flex items-center justify-center"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingReader(null)}
                            className="btn-secondary flex-1 inline-flex items-center justify-center"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{type.name}</div>
                          <div className="text-sm text-slate-600">
                            Monthly: ${(type.monthly_cost || type.cost_per_unit || 0).toFixed(2)}/mo
                          </div>
                          {type.has_sim && (
                            <div className="text-sm text-blue-600 mt-1">
                              📱 SIM: ${(type.sim_cost || 0).toFixed(2)}/month
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setEditingReader({ 
                              id: type.id, 
                              name: type.name, 
                              cost_per_unit: type.cost_per_unit || 0,
                              monthly_cost: type.monthly_cost || type.cost_per_unit || 0,
                              has_sim: type.has_sim || false,
                              sim_cost: type.sim_cost || 0
                            })}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteReaderId(type.id || type.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                            data-testid={`delete-reader-${type.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {/* Inline confirmation — replaces window.confirm (blocked in most envs) */}
                          {confirmDeleteReaderId === (type.id || type.name) && (
                            <div className="flex items-center space-x-1 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                              <span className="text-xs text-red-700 font-medium">Delete?</span>
                              <button
                                onClick={() => handleDeleteReaderType(type.id || type.name)}
                                className="p-1 text-white bg-red-600 hover:bg-red-700 rounded text-xs font-bold"
                                data-testid={`confirm-delete-reader-${type.name}`}
                              >Yes</button>
                              <button
                                onClick={() => setConfirmDeleteReaderId(null)}
                                className="p-1 text-slate-600 hover:bg-slate-100 rounded text-xs"
                              >No</button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Billing Frequencies */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Billing Frequencies</h2>
            
            {/* Add New */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <label className="block text-sm font-medium text-slate-700 mb-2">Add New Billing Frequency</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newBillingFreq}
                  onChange={(e) => setNewBillingFreq(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddBillingFreq()}
                  placeholder="Enter billing frequency..."
                  className="input-field flex-1"
                />
                <button onClick={handleAddBillingFreq} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </button>
              </div>
            </div>

            {/* List */}
            <div className="space-y-2">
              {billingFrequencies.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No billing frequencies found</p>
              ) : (
                billingFrequencies.map((freq, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <span className="font-medium text-slate-900">{freq}</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setConfirmDeleteFreqId(freq)}
                        className="text-red-600 hover:text-red-700 p-2"
                        title="Delete"
                        data-testid={`delete-freq-${freq}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {confirmDeleteFreqId === freq && (
                        <div className="flex items-center space-x-1 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                          <span className="text-xs text-red-700 font-medium">Delete?</span>
                          <button onClick={() => handleDeleteBillingFreq(freq)} className="p-1 text-white bg-red-600 hover:bg-red-700 rounded text-xs font-bold">Yes</button>
                          <button onClick={() => setConfirmDeleteFreqId(null)} className="p-1 text-slate-600 hover:bg-slate-100 rounded text-xs">No</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
